import {
  Component,
  effect,
  inject,
  OnInit,
  ChangeDetectionStrategy,
  signal,
  untracked,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { CrispService } from './core/services/crisp.service';
import { StoredConsentService } from './core/services/stored-consent.service';
import { WebshopSessionService } from './core/services/webshop-session.service';
import { isBrowserPlatform } from './core/utils/platform';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.scss',
})
export class App implements OnInit {
  private crispService = inject(CrispService);
  private sessionService = inject(WebshopSessionService);
  private storedConsent = inject(StoredConsentService);

  private readonly isBrowser = isBrowserPlatform();

  /** The session is known and the visitor is not staff — the point from which the chat may go. */
  private crispWanted = signal(false);
  private crispInitialized = false;

  constructor() {
    effect(() => {
      const personId = this.sessionService.customerPersonId();
      if (!this.crispInitialized) return;

      if (personId && !this.sessionService.isInternalUser()) {
        void this.setCrispIdentity(personId);
      } else if (!personId) {
        this.crispService.clearSession();
      }
    });

    // Crisp sets storage of its own, so it waits for analytics consent — and the banner's answer
    // can come minutes after boot, which is why this is an effect on the stored consent and not a
    // one-off check in `ngOnInit`.
    effect(() => {
      if (!this.crispWanted() || !this.storedConsent.consent()?.analytics) return;
      if (this.crispInitialized) return;
      untracked(() => void this.initCrisp());
    });
  }

  async ngOnInit(): Promise<void> {
    // None of this belongs to the prerendered HTML: the session is a cookie the build does not
    // have, and the chat widget is a script only a real visitor can load.
    if (!this.isBrowser) return;

    // `whenReady`, not a request of our own: the app initialiser has already asked the ERP who is
    // looking and bootstrap held for the answer, so by the time this runs the signals below are
    // populated and this resolves immediately. Calling `getWebshopSessionCustomerPersonId` here
    // sent a second, identical request on every single page load — a whole round trip to another
    // origin, issued in the middle of the first render, for an answer the app already had.
    await this.sessionService.whenReady();

    if (this.sessionService.isInternalUser()) return;

    this.crispWanted.set(true);
  }

  private async initCrisp(): Promise<void> {
    const personId = this.sessionService.customerPersonId();
    if (personId) {
      await this.setCrispIdentity(personId);
    }

    this.crispService.setLocale();
    this.crispService.show();
    this.crispInitialized = true;
  }

  private async setCrispIdentity(personId: number): Promise<void> {
    // The same request the header makes for the visitor's name, answered once for both.
    const person = await this.sessionService.loadCustomerPerson(personId);
    if (!person) return;

    // Who the session belongs to can have changed while that was in flight — a sign-out and a
    // sign-in as somebody else is all it takes. The promise resolves with the profile it was
    // asked for regardless, and handing it to Crisp would open the chat under the previous
    // customer's name and e-mail address.
    if (this.sessionService.customerPersonId() !== personId) return;

    if (person.email) {
      this.crispService.setTokenId(person.email);
    }
    this.crispService.setUserProfile(person);
  }
}
